import PartsFragaList from '@/components/_common/PartsFragaList';
import { FragaProduct } from '@/core/domain/operational/Fraga/Fraga.types';
import { Modal, ModalProps, Typography } from 'antd';
import { PartFormData } from '../PartForm';
import { useEffect, useState } from 'react';
import { useManufacturersMutation } from '@/core/domain/operational/Manufacturers/Manufacturers.hooks';
import { useSubGroupsMutation } from '@/core/domain/operational/SubGroups/SubGroups.hooks';
import { useNcmsMutation } from '@/core/domain/operational/Ncm/Ncm.hooks';
import { createUploadFileObj } from '@/core/domain/archive/Archive/Archive.utils';
import ModalLoadingOverlay from '@/components/_common/ModalLoadingOverlay';

interface Props extends Omit<ModalProps, 'onOk' | 'onCancel'> {
  onCancel: () => void;
  onCallbackValuesForm: (values: PartFormData) => void;
  productId?: string;
}

export default function CreatePartByFragaModal({
  onCancel,
  onCallbackValuesForm,
  productId,
  ...props
}: Props) {
  const [productSelect, setProductSelect] = useState<FragaProduct>();

  const manufacturersMutation = useManufacturersMutation();
  const subGroupsMutation = useSubGroupsMutation();
  const ncmsMutation = useNcmsMutation();

  async function handleGetManufacturer(manufacturerName?: string) {
    if (manufacturerName) {
      const manufacturers = await manufacturersMutation.mutateAsync({
        name: manufacturerName,
      });

      const manufacturer = manufacturers.data.find(
        (item) =>
          item.name.toLocaleLowerCase() ===
          manufacturerName.toLocaleLowerCase(),
      );

      if (manufacturer) {
        return manufacturer;
      }

      return undefined;
    }

    return undefined;
  }

  async function handleGetSubGroup(subGroupName?: string) {
    if (subGroupName) {
      const subGroups = await subGroupsMutation.mutateAsync({
        name: subGroupName,
      });

      const subGroup = subGroups.data.find(
        (item) =>
          item.name.toLocaleLowerCase() === subGroupName.toLocaleLowerCase(),
      );

      if (subGroup) {
        return subGroup;
      }

      return undefined;
    }

    return undefined;
  }

  async function handleGetNcm(ncmDescription?: string) {
    if (ncmDescription) {
      const ncms = await ncmsMutation.mutateAsync({
        code: ncmDescription,
      });

      const ncmCodeFormated = parseInt(ncmDescription.replace(/\./g, ''));

      const ncm = ncms.data.find((item) => item.code === ncmCodeFormated);

      if (ncm) {
        return ncm;
      }

      return undefined;
    }
    return undefined;
  }

  useEffect(() => {
    (async () => {
      if (productSelect) {
        let values = {} as PartFormData;

        if (productSelect.brand.name) {
          const manufacturer = await handleGetManufacturer(
            productSelect.brand.name,
          );

          if (manufacturer) {
            values = { ...values, manufacturerId: manufacturer.id };
          }
        }

        if (productSelect?.productGroup?.family?.name) {
          const subGroup = await handleGetSubGroup(
            productSelect?.productGroup?.family?.name,
          );

          if (subGroup) {
            values = { ...values, subGroupId: subGroup.id };
          }
        }

        if (productSelect?.specifications?.length) {
          const currentItemNcm = productSelect?.specifications?.find(
            (item) => item.description === 'NCM',
          )?.value;

          const ncm = await handleGetNcm(currentItemNcm);

          if (ncm) {
            values = { ...values, ncmId: ncm.id };
          }
        }

        const image = productSelect?.images?.length
          ? createUploadFileObj(productSelect?.images[0]?.thumbnailUrl)
          : undefined;

        const comment = productSelect.specifications
          ?.map((item) => `${item.description}: ${item.value}`)
          .join(`\n`);

        onCallbackValuesForm({
          ...values,
          catalogProductId: productSelect.productId,
          active: true,
          manufacturingCode: productSelect.partNumber,
          name: productSelect.applicationDescription,
          barCode: productSelect.specifications?.length
            ? productSelect.specifications?.find((item) =>
                item.description.includes('(EAN)'),
              )?.value
            : '',
          imageUrl: image ? [image] : [],
          comment,
        });

        onCancel();
      }
    })();
  }, [productSelect]);

  return (
    <Modal
      {...props}
      width={900}
      title="Selecionar Peça Fraga"
      footer={[]}
      onCancel={onCancel}
    >
      {(manufacturersMutation.isLoading ||
        subGroupsMutation.isLoading ||
        ncmsMutation.isLoading) && (
        <ModalLoadingOverlay
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            backdropFilter: 'blur(2px)',
          }}
          messages={['Preenchendo formulário...']}
        />
      )}

      <Typography.Paragraph>
        Digite no campo abaixo para listar peças por informações essenciais da
        peça. Ex: Modelo, Marca, Nome da peça etc.
      </Typography.Paragraph>

      <PartsFragaList
        onCallback={setProductSelect}
        productSelectId={productId}
      />
    </Modal>
  );
}
